import { NavLink, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AlertBadge from '../alerts/AlertBadge';
import { useWebSocket } from '../../api/websocket';
import { useEffect, useState } from 'react';
import RadioPlayer from '../dashboard/RadioPlayer';
import StatusBar from './StatusBar';

const DJNavigation = [
  { name: 'Panel', to: '/dashboard', icon: '📊' },
  { name: 'Mis Series', to: '/shows', icon: '📺' },
  { name: 'Mis Episodios', to: '/episodes', icon: '🎬' },
  { name: 'Alertas', to: '/alerts', icon: '🔔' },
  { name: 'Historial', to: '/history/music', icon: '📚' },
];

const DJLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [nowPlaying, setNowPlaying] = useState(null);

  const { subscribe } = useWebSocket((data) => {
    if (data.type === 'now_playing') {
      setNowPlaying(data.payload);
    }
  });

  useEffect(() => {
    const unsub = subscribe('now_playing', (data) => setNowPlaying(data.payload));
    return unsub;
  }, [subscribe]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen overflow-hidden bg-dark-900 text-dark-100">
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={closeSidebar}></div>
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 overflow-y-auto bg-dark-850 border-r border-dark-700 transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-dark-700">
          <h1 className="text-xl font-bold text-secondary-400">AUTODJ RADIO</h1>
          <button onClick={closeSidebar} className="lg:hidden text-dark-400 hover:text-dark-200">
            ✕
          </button>
        </div>

        <nav className="py-2">
          {DJNavigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm font-medium ${
                  isActive
                    ? 'bg-secondary-900/20 text-secondary-400 border-l-2 border-secondary-500'
                    : 'text-dark-300 hover:bg-dark-800 hover:text-dark-100'
                }`
              }
            >
              <span className="w-5 text-center">{item.icon}</span>
              <span>{item.name}</span>
              {item.to === '/alerts' && <AlertBadge />}
            </NavLink>
          ))}
        </nav>

        {/* Now Playing */}
        {nowPlaying && (
          <div className="border-t border-dark-700 p-4">
            <div className="flex items-center gap-2">
              <span className="text-lg">🎧</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-dark-100">{nowPlaying.title}</p>
                <p className="text-xs text-dark-400">{nowPlaying.artist}</p>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-dark-700 p-4">
          <div className="flex items-center gap-3">
            <img
              src={user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'DJ')}&background=0891b2&color=fff`}
              alt={user?.name}
              className="h-10 w-10 rounded-full"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-dark-100">DJ {user?.name}</p>
              <p className="text-xs text-dark-400">DJ</p>
            </div>
            <button
              onClick={logout}
              className="text-xs text-dark-400 hover:text-dark-200"
              title="Cerrar sesión"
            >
              ↪
            </button>
          </div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between bg-dark-850 border-b border-dark-700 px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="rounded-md p-2 text-dark-400 hover:bg-dark-800 hover:text-dark-200 lg:hidden"
            >
              ☰
            </button>
            <h2 className="text-lg font-semibold text-dark-100">
              {DJNavigation.find((n) => location.pathname.startsWith(n.to))?.name || 'AUTODJ RADIO'}
            </h2>

            {nowPlaying && (
              <div className="flex items-center gap-2 rounded-md bg-dark-800 px-3 py-1 text-sm">
                <span className="text-success-400">● EN VIVO</span>
                <span className="text-dark-300">{nowPlaying.artist} - {nowPlaying.title}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <RadioPlayer compact />
            <Link to="/alerts" className="relative text-dark-400 hover:text-warning-400">
              🔔
              <AlertBadge />
            </Link>
          </div>
        </header>

        <StatusBar canControl={false} />

        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DJLayout;
